import React, {Dispatch, SetStateAction, useEffect, useRef} from 'react';
import {FormattedMessage} from 'react-intl';
import CloseButton from './CloseButton';
import BISSELLNotice from './BISSELLNotice';
interface ModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  noticeId: string;
  locale: string;
}

declare global {
  interface Window {
    BISSELLNotice?: (options: BISSELLNoticeOptions) => void;
  }
}

interface BISSELLNoticeOptions {
  entryId: string;
  sidebar: boolean;
  locale: string;
  fallbackLocale?: string;
}

const TermsModal: React.FC<ModalProps> = ({
  isOpen,
  setIsOpen,
  noticeId,
  locale,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        return;
      }

      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, setIsOpen]);

  if (!isOpen) return null;

  return (
    <div className='flex justify-center h-screen bg-gray-500'>
      <div
        ref={modalRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby='modal-title'
        tabIndex={-1}
        className='fixed z-10 inset-y-10 overflow-y-scroll justify-center w-11/12 focus:outline-none'>
        <div className='bg-white rounded-lg z-50 mt-0 pt-5 self-center w-auto'>
          <h2 id='modal-title' className='sr-only'>
            <FormattedMessage id='Aria.LegalNotice' />
          </h2>
          <CloseButton
            onClose={() => setIsOpen(false)}
            closeButtonRef={closeButtonRef}
          />
          {BISSELLNotice({
            entryId: noticeId,
            sidebar: true,
            locale: locale,
          })}
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
